import {
  PDF_HIGHLIGHT_CURRENT_FILL,
  PDF_HIGHLIGHT_FILL,
  PDF_HIGHLIGHT_UNDERLINE,
  PDF_PAGE_BACKGROUND,
  PDF_PAGE_GAP_PX,
  PDF_PAGE_PLACEHOLDER_BACKGROUND,
  PDF_PAGE_PLACEHOLDER_COLOR,
  PDF_PAGE_PLACEHOLDER_FONT_SIZE,
  PDF_PAGE_SHADOW,
} from '@/constants/pdf';

/**
 * The page stack is built imperatively rather than in JSX: pdf.js paints into a
 * canvas it does not own, and the placeholders have to exist at their final
 * height before a page renders so the scroll offsets are correct from the
 * first frame.
 */

const applyPageChrome = (pageContainer: HTMLDivElement, pageNum: number) => {
  pageContainer.setAttribute('data-page', String(pageNum));
  pageContainer.style.position = 'relative';
  pageContainer.style.flexShrink = '0';
  pageContainer.style.marginBottom = `${PDF_PAGE_GAP_PX}px`;
  pageContainer.style.boxShadow = PDF_PAGE_SHADOW;
};

export const createPageContainer = (pageNum: number): HTMLDivElement => {
  const pageContainer = document.createElement('div');
  applyPageChrome(pageContainer, pageNum);
  pageContainer.style.background = PDF_PAGE_BACKGROUND;
  return pageContainer;
};

export const createPlaceholderPageContainer = (
  pageNum: number,
  width: number,
  height: number,
): HTMLDivElement => {
  const pageContainer = document.createElement('div');
  applyPageChrome(pageContainer, pageNum);
  pageContainer.style.width = `${width}px`;
  pageContainer.style.height = `${height}px`;
  pageContainer.style.background = PDF_PAGE_PLACEHOLDER_BACKGROUND;
  pageContainer.style.display = 'flex';
  pageContainer.style.alignItems = 'center';
  pageContainer.style.justifyContent = 'center';

  const placeholder = document.createElement('div');
  placeholder.style.color = PDF_PAGE_PLACEHOLDER_COLOR;
  placeholder.style.fontSize = PDF_PAGE_PLACEHOLDER_FONT_SIZE;
  // FIXME(i18n): hardcoded English in a Japanese-first UI. Needs a locale key.
  placeholder.textContent = `Page ${pageNum}`;
  pageContainer.appendChild(placeholder);

  return pageContainer;
};

export const createHighlightLayer = (): HTMLDivElement => {
  const highlightLayer = document.createElement('div');
  highlightLayer.className = 'highlight-layer';
  highlightLayer.style.position = 'absolute';
  highlightLayer.style.top = '0';
  highlightLayer.style.left = '0';
  highlightLayer.style.right = '0';
  highlightLayer.style.bottom = '0';
  highlightLayer.style.pointerEvents = 'none';
  return highlightLayer;
};

/** pdf.js hands back a canvas we must place ourselves; UA margins would offset it. */
export const seatCanvas = (canvas: HTMLCanvasElement) => {
  canvas.style.display = 'block';
  canvas.style.margin = '0';
  canvas.style.padding = '0';
};

interface HighlightRectArgs {
  left: number;
  top: number;
  width: number;
  height: number;
  isCurrent: boolean;
  onSelect: () => void;
}

export const createHighlightRect = ({
  left, top, width, height, isCurrent, onSelect,
}: HighlightRectArgs): HTMLDivElement => {
  const highlight = document.createElement('div');
  highlight.style.position = 'absolute';
  highlight.style.left = `${left}px`;
  highlight.style.top = `${top}px`;
  highlight.style.width = `${width}px`;
  highlight.style.height = `${height}px`;
  highlight.style.backgroundColor = isCurrent ? PDF_HIGHLIGHT_CURRENT_FILL : PDF_HIGHLIGHT_FILL;
  highlight.style.mixBlendMode = 'multiply';
  highlight.style.borderBottom = PDF_HIGHLIGHT_UNDERLINE;
  highlight.style.pointerEvents = 'auto';
  highlight.style.cursor = 'pointer';
  highlight.onclick = onSelect;
  return highlight;
};
